import { faCopy, faEye, faEyeSlash } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  Modal,
  QuestInstance,
  QuestSeries,
  SPACING,
  TherapistIcon,
  Typography,
} from "@joonapp/web-shared"
import { CloseIcon } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useRef, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { ChildQuestStatusCard } from "./ChildQuestStatusCard"
import { DeleteQuestButton, EditQuestButton } from "./QuestButtonComponents"
import { QuestDetails } from "./QuestDetails"
import QuestLog from "./QuestLog"
import { useViewQuestModalStore } from "./useViewQuestModalStore"
import AttachmentItem from "../../../components/attachmentItem/AttachmentItem"
import { useAddEditQuestStore } from "../addEditQuestModal/useAddEditQuestStore"
import ToggleQuestActiveModal, {
  useToggleQuestActiveModalStore,
  ToggleQuestActiveModalType,
} from "../ToggleQuestActiveModal"
import {
  useGetQuestGroupSeriesIds,
  useQuestGroupInstances,
} from "../useQuestGroups"

const ViewQuestModal = () => {
  const { isOpen, onClose } = useViewQuestModalStore()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      animate
      mobileFull
      style={{ width: "min(500px, 100vw)" }}
    >
      <ViewQuestModalContent />
    </Modal>
  )
}

export default ViewQuestModal

const ViewQuestModalContent = () => {
  const { onClose, questInstance } = useViewQuestModalStore()
  const { questGroupInstances } = useQuestGroupInstances(questInstance)
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState(false)

  // Track scroll position
  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const handleScroll = () => {
      const scrollTop = scrollElement.scrollTop
      setIsScrolled(scrollTop > 0)
    }

    scrollElement.addEventListener("scroll", handleScroll)

    // Clean up event listener
    return () => {
      scrollElement.removeEventListener("scroll", handleScroll)
    }
  }, [scrollRef])

  if (!questInstance) return <></>

  const questSeriesData = questInstance.series
  const frequencyTarget = questSeriesData.current_frequency_target

  return (
    <>
      <button className="absolute top-[18px] right-6 z-[250]" onClick={onClose}>
        <CloseIcon size={24} color={JoonUIColor.icon.inverted} />
      </button>

      <div className="overflow-y-auto flex-1" ref={scrollRef}>
        <StickyHeader
          questSeriesData={questSeriesData}
          questInstance={questInstance}
          isScrolled={isScrolled}
        />

        <div className="flex flex-col gap-4 py-4 px-6">
          {!!frequencyTarget && (
            <FlexBox
              direction="row"
              justify="space-between"
              align="center"
              wrap={false}
              gap={SPACING.space2}
              style={{
                border: `1px solid ${JoonUIColor.border.accent}`,
                borderRadius: SPACING.space2,
                padding: SPACING.space3,
                background: JoonUIColor.background.primaryNeutral,
              }}
            >
              <FlexBox direction="column" gap={SPACING.space1}>
                <Typography variant="bodySmallBold" textAlign="left">
                  {t("VQ_FREQUENCY_TARGET", {
                    count: frequencyTarget.frequency,
                  })}
                </Typography>
                <Typography variant="bodySmall" textAlign="left">
                  {t("VQ_GOAL_SET_BY", {
                    name:
                      frequencyTarget.creator_profile?.nickname ||
                      t("VQ_THERAPIST_DEFAULT"),
                    date: dayjs(frequencyTarget.date_created).format(
                      "MMMM D, YYYY"
                    ),
                  })}
                </Typography>
              </FlexBox>

              <div style={{ minWidth: "32px" }}>
                <TherapistIcon
                  size={32}
                  color={JoonUIColor.background.accent}
                />
              </div>
            </FlexBox>
          )}

          <FlexBox direction="column" gap={SPACING.space1}>
            <Typography variant="bodySmallBold">
              {t("VQ_CURRENT_STATUS")}
            </Typography>
            {questGroupInstances.map((quest) => (
              <ChildQuestStatusCard key={quest.id} questInstance={quest} />
            ))}
          </FlexBox>
          {questInstance?.series?.resource && (
            <AttachmentItem
              name={questInstance.series.resource.title}
              url={questInstance.series.resource.resource_url}
            />
          )}
          <FlexBox direction="column" gap={SPACING.space1}>
            <Typography variant="bodySmallBold">
              {t("VQ_QUEST_DETAILS")}
            </Typography>
            <QuestDetails />
          </FlexBox>
          <FlexBox direction="column" gap={SPACING.space1}>
            <Typography variant="bodySmallBold">{t("VQ_LOGS")}</Typography>
            <QuestLog />
          </FlexBox>
        </div>
      </div>

      <div
        style={{
          position: "sticky",
          bottom: "0",
          width: "100%",
          background: "white",
          borderTop: `1px solid ${JoonUIColor.border.default}`,
          boxShadow: "0 -6px 8px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div className="flex flex-row items-center justify-center py-4 px-4">
          <ToggleActiveButton />
          <DeleteQuestButton />
          <DuplicateQuestButton />
          <EditQuestButton />
        </div>
      </div>
    </>
  )
}

const StickyHeader = ({
  questSeriesData,
  questInstance,
  isScrolled,
}: {
  questSeriesData: QuestSeries
  questInstance: QuestInstance
  isScrolled: boolean
}) => {
  const titleRef = useRef<HTMLDivElement>(null)
  const titleHeight = titleRef.current?.clientHeight
  const collapsedHeaderOffset = titleHeight ? -200 + titleHeight + 32 : 0

  return (
    <div
      className="w-full min-h-[200px] flex  p-4 px-6 flex-col gap-2 justify-end"
      style={{
        backgroundColor: questSeriesData.photo_url
          ? "transparent"
          : JoonColorExpanded.indigo400,
        backgroundImage: questSeriesData.photo_url
          ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${questSeriesData.photo_url})`
          : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "sticky",
        top: collapsedHeaderOffset,
        zIndex: 200,
      }}
    >
      <div ref={titleRef}>
        <Typography
          variant="h3"
          color={JoonUIColor.text.inverted}
          className="break-words mr-10"
        >
          {questSeriesData.title}
        </Typography>
      </div>
      {questInstance.series.description && !isScrolled && (
        <Typography variant="bodySmall" color={JoonUIColor.text.inverted}>
          {questInstance.series.description}
        </Typography>
      )}
    </div>
  )
}

const ToggleActiveButton = () => {
  const { questInstance, onClose: closeViewQuestModal } =
    useViewQuestModalStore()
  const { onOpen: openToggleQuestActiveModal } =
    useToggleQuestActiveModalStore()
  const getQuestGroupSeriesIds = useGetQuestGroupSeriesIds()
  const { t } = useTranslation()
  const isActive = questInstance?.series.is_active

  const onClickToggleActive = () => {
    if (isActive) {
      const questGroupSeriesIds = getQuestGroupSeriesIds(
        questInstance.series.id
      )
      openToggleQuestActiveModal(
        questGroupSeriesIds,
        ToggleQuestActiveModalType.INACTIVE
      )
    } else {
      closeViewQuestModal()
    }
  }

  return (
    <button
      className="flex flex-col gap-2 items-center p-3 flex-1"
      onClick={onClickToggleActive}
    >
      <FontAwesomeIcon
        icon={isActive ? faEyeSlash : faEye}
        fontSize={24}
        color={JoonUIColor.text.disabled}
      />
      <Typography variant="bodySmallBold" color={JoonUIColor.text.disabled}>
        {isActive ? t("QB_SET_INACTIVE") : t("VQ_ACTIVATE")}
      </Typography>
      <ToggleQuestActiveModal />
    </button>
  )
}

const DuplicateQuestButton = () => {
  const { questInstance, onClose } = useViewQuestModalStore()
  const {
    setQuestName,
    setSelectedQuest,
    onOpen: openAddEditQuestModal,
    setPhotoFile,
    setDuplicateQuest,
  } = useAddEditQuestStore()
  const { t } = useTranslation()

  const onClickDuplicateQuest = async () => {
    if (!questInstance) return
    openAddEditQuestModal(questInstance.series)

    setTimeout(async () => {
      setSelectedQuest(null)
      setQuestName(questInstance.series.title + t("VQ_COPY_SUFFIX"))
      setDuplicateQuest(questInstance)
      // Copy photo
      const URL = questInstance.series.photo_url
      if (URL) {
        const response = await fetch(URL)
        const blob = await response.blob()
        const file = new File([blob], "image.png", { type: "image/png" })
        setPhotoFile(file)
      }
    }, 100)
    onClose()
  }

  return (
    <button
      className="flex flex-col gap-2 items-center p-3 flex-1"
      onClick={onClickDuplicateQuest}
    >
      <FontAwesomeIcon
        icon={faCopy}
        fontSize={24}
        color={JoonUIColor.text.disabled}
      />
      <Typography variant="bodySmallBold" color={JoonUIColor.text.disabled}>
        {t("VQ_DUPLICATE")}
      </Typography>
    </button>
  )
}
